import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  FormContainer,
  FormEmojiTextInput,
  FormInput,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { useCallback } from "react";
import { UserSelector } from "./UserSelector/UserSelector";
import { TicketWorkspaceSelector } from "./TicketWorkspaceSelector/TicketWorkspaceSelector";
import { TicketChannelSelector } from "./TicketChannelSelector/TicketChannelSelector";
import {
  createQuickAnswer,
  getQuickAnswer,
  updateQuickAnswer,
} from "../../../../../../services/quickAnswer.service";
import { quickAnswerSchema, TQuickAnswerForm } from "./QuickAnswerSchema";
import { INewQuickAnswer } from "../../../../../../models/quick-answer";
import { ShareTypeSelector } from "./ShareTypeSelector/ShareTypeSelector";

const translationPath = "page.settings.quickAnswer.";

const QuickAnswer = () => {
  const { t } = useTranslation();

  const { sendRequest: save, loading: sLoading } = useFetch(createQuickAnswer);

  const { sendRequest: update, loading: uLoading } =
    useFetch(updateQuickAnswer);
  const { sendRequest: get, loading: gLoading } = useFetch(getQuickAnswer);

  const quickAnswerFormData = useCallback(
    (form: TQuickAnswerForm): INewQuickAnswer => {
      return {
        ...form,
        ticketChannelId:
          form.ticketChannel && form.shareType.code === "3"
            ? form.ticketChannel.id
            : undefined,
        ticketWorkspaceId:
          form.ticketWorkspace && form.shareType.code === "2"
            ? form.ticketWorkspace.id
            : undefined,
        userId:
          form.user && form.shareType.code === "1" ? form.user.id : undefined,
        shareTypeId: form.shareType.id,
      };
    },
    []
  );

  const handleSave = (v: TQuickAnswerForm) => {
    return save(quickAnswerFormData(v));
  };

  const handleUpdate = (v: TQuickAnswerForm, id: string) => {
    return update({
      item: quickAnswerFormData(v),
      id: id,
    });
  };

  const handleGet = useCallback(
    async (id: string): Promise<Treturn<TQuickAnswerForm>> => {
      const { data, success } = await get(id);
      if (data) {
        return {
          data: {
            ...data,
            user: data.user
              ? { id: data.user.id, name: data.user.fullName }
              : { id: "", name: "" },
          },
          success: true,
        };
      }
      return { data, success };
    },
    [get]
  );

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<TQuickAnswerForm>
      saveFunction={handleSave}
      updateFunction={handleUpdate}
      getFunction={handleGet}
      loading={loading}
      title={t(translationPath + "quick_answer")}
      schema={quickAnswerSchema}
      subtitle={t(translationPath + "new_quick_answer")}
      subtitleWatchField={"title"}
      fields={() => (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormInput
              name="title"
              label={t(translationPath + "title")}
              size="small"
              fullWidth
              autoFocus
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormInput
              name="shortCut"
              label={t(translationPath + "shortcut")}
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <FormEmojiTextInput
              name="message"
              label={t(translationPath + "message")}
              size="small"
              fullWidth
              multiline
              minRows={4}
            />
          </Grid>
          <ShareTypeSelector />
          <UserSelector />
          <TicketWorkspaceSelector />
          <TicketChannelSelector />
        </Grid>
      )}
    />
  );
};

export default QuickAnswer;
